import { useCallback, useEffect, useState } from 'react';
import { LEAD_STAGES, type Api, type Contact, type LeadScore, type LeadStage } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';
import { ReadOnlyNote } from '../ui';
import { errorText } from './AgentSettings';

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

export const ScoreBadge = ({ score, t }: { score: LeadScore | null | undefined; t: T }) =>
  score ? <span className={`score ${score}`}>{t(`leads.score.${score}`)}</span> : null;

/** Sales pipeline (ADR-029): contacts by stage. Calls move them automatically; the team moves them by drag or menu. */
export const LeadsPage = ({ api, t, onOpenCall }: { api: Api; t: T; onOpenCall: (id: string) => void }) => {
  // ADR-071: moving a lead (drag or menu) needs centeros.contacts; without it the board is view-only.
  const canMove = useCan()(PERM.contacts);
  const [rows, setRows] = useState<Contact[]>();
  const [q, setQ] = useState('');
  const [error, setError] = useState<string>();
  const [dragging, setDragging] = useState<string>();
  const [over, setOver] = useState<LeadStage>();

  const load = useCallback(() => {
    api.contacts(q, 2000).then((r) => setRows(r.contacts)).catch((e: unknown) => setError(errorText(t, e)));
  }, [api, q, t]);
  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [load]);

  const move = async (id: string, stage: LeadStage) => {
    const before = rows;
    setRows((r) => r?.map((c) => (c.id === id ? { ...c, stage, stageManual: true } : c)));
    try {
      await api.setContactStage(id, stage);
    } catch (e) {
      setRows(before);
      setError(errorText(t, e));
    }
  };

  const byStage = (s: LeadStage) => (rows ?? []).filter((c) => (c.stage ?? 'new') === s);
  const hot = (rows ?? []).filter((c) => c.score === 'hot' && c.stage !== 'won' && c.stage !== 'lost').length;

  return (
    <section>
      <header className="page-head">
        <div>
          <h1>{t('leads.title')}</h1>
          <p className="muted">{t('leads.subtitle', { n: rows?.length ?? 0, hot })}</p>
        </div>
      </header>
      <div className="toolbar">
        <input className="search" type="search" value={q} placeholder={t('contacts.search')} onChange={(e) => setQ(e.target.value)} />
      </div>
      <ReadOnlyNote t={t} need={PERM.contacts} />
      {error && <div className="banner error small">{error}</div>}
      {rows && rows.length === 0 && <div className="empty"><p>{t('leads.empty')}</p></div>}
      {rows && rows.length > 0 && (
        <div className="kanban">
          {LEAD_STAGES.map((s) => {
            const items = byStage(s);
            return (
              <div
                key={s}
                className={`lane ${over === s ? 'over' : ''}`}
                onDragOver={(e) => { if (!canMove) return; e.preventDefault(); setOver(s); }}
                onDragLeave={() => setOver((o) => (o === s ? undefined : o))}
                onDrop={(e) => {
                  e.preventDefault();
                  if (!canMove) return;
                  setOver(undefined);
                  const c = rows.find((x) => x.id === dragging);
                  if (c && (c.stage ?? 'new') !== s) void move(c.id, s);
                  setDragging(undefined);
                }}
              >
                <div className="lane-head">
                  <span className={`stage-dot ${s}`} />
                  <strong>{t(`leads.stage.${s}`)}</strong>
                  <span className="muted num">{items.length}</span>
                </div>
                <div className="lane-body">
                  {items.map((c) => (
                    <article key={c.id} className="lead" draggable={canMove} onDragStart={() => setDragging(c.id)} onDragEnd={() => setDragging(undefined)}>
                      <div className="row-between">
                        <strong className="lead-name">{c.name || c.phone}</strong>
                        <ScoreBadge score={c.score} t={t} />
                      </div>
                      {c.name && <div className="mono small muted">{c.phone}</div>}
                      {c.lastSummary && <p className="lead-summary">{c.lastSummary}</p>}
                      <div className="row-between lead-foot">
                        {c.lastCallId ? (
                          <button type="button" className="link small" onClick={() => onOpenCall(c.lastCallId!)}>
                            {c.lastContactedAt ? when(c.lastContactedAt) : t('leads.openCall')}
                          </button>
                        ) : <span className="muted small">{t('leads.notCalled')}</span>}
                        {canMove && (
                          <select aria-label={t('leads.moveTo')} value={c.stage ?? 'new'} onChange={(e) => void move(c.id, e.target.value as LeadStage)}>
                            {LEAD_STAGES.map((x) => <option key={x} value={x}>{t(`leads.stage.${x}`)}</option>)}
                          </select>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
